import { supabase } from '../utils/supabase';

/**
 * Inquiry Service
 * Handles vendor inquiries and messages
 */
class InquiryService {
  /**
   * Get inquiries for a vendor
   */
  async getVendorInquiries(vendorId, { status, limit = 50, offset = 0 } = {}) {
    try {
      let query = supabase
        .from('vendor_inquiries')
        .select(`
          *,
          user:user_profiles!user_id(
            id,
            full_name,
            email,
            phone
          )
        `, { count: 'exact' })
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error, count } = await query
        .range(offset, offset + limit - 1);

      if (error) {
        // Table might not exist yet
        if (error.code === 'PGRST116' || error.message.includes('does not exist')) {
          return { success: true, data: [], total: 0 };
        }
        throw error;
      }

      return {
        success: true,
        data,
        count
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get inquiries for a user
   */
  async getUserInquiries(userId, { limit = 50, offset = 0 } = {}) {
    try {
      const { data, error } = await supabase
        .from('vendor_inquiries')
        .select(`
          *,
          vendor:vendor_profiles!vendor_id(id, business_name, category, phone, email)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        // Table might not exist yet
        if (error.code === 'PGRST116' || error.message.includes('does not exist')) {
          return { success: true, data: [] };
        }
        throw error;
      }

      return {
        success: true,
        data,
        count
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Create a new inquiry
   */
  async createInquiry(inquiryData) {
    try {
      const {
        vendorId,
        userId,
        subject,
        message,
        eventDate,
        eventType,
        guestCount,
        budget,
        contactPhone,
        preferredContactMethod
      } = inquiryData;

      const { data, error } = await supabase
        .from('vendor_inquiries')
        .insert({
          vendor_id: vendorId,
          user_id: userId,
          subject: subject || 'New Inquiry',
          message,
          event_date: eventDate,
          event_type: eventType,
          guest_count: guestCount,
          budget,
          contact_phone: contactPhone,
          preferred_contact_method: preferredContactMethod || 'email',
          status: 'unread',
          priority: guestCount >= 200 ? 'high' : 'medium'
        })
        .select()
        .single();

      if (error) throw error;

      return {
        success: true,
        data
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Update inquiry status
   */
  async updateInquiryStatus(inquiryId, status) {
    try {
      const { data, error } = await supabase
        .from('vendor_inquiries')
        .update({
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', inquiryId)
        .select()
        .single();

      if (error) throw error;

      return {
        success: true,
        data
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Reply to an inquiry
   */
  async replyToInquiry(inquiryId, replyMessage, vendorId) {
    try {
      const { data, error } = await supabase
        .from('vendor_inquiries')
        .update({
          status: 'replied',
          reply_message: replyMessage,
          replied_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', inquiryId)
        .eq('vendor_id', vendorId)
        .select()
        .single();

      if (error) throw error;

      return {
        success: true,
        data
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Archive inquiries
   */
  async archiveInquiries(inquiryIds, vendorId) {
    try {
      const { data, error } = await supabase
        .from('vendor_inquiries')
        .update({
          status: 'archived',
          updated_at: new Date().toISOString()
        })
        .in('id', inquiryIds)
        .eq('vendor_id', vendorId)
        .select();

      if (error) throw error;

      return {
        success: true,
        data
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Delete inquiry
   */
  async deleteInquiry(inquiryId, vendorId) {
    try {
      const { error } = await supabase
        .from('vendor_inquiries')
        .delete()
        .eq('id', inquiryId)
        .eq('vendor_id', vendorId);

      if (error) throw error;

      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get inquiry by ID
   */
  async getInquiryById(inquiryId) {
    try {
      const { data, error } = await supabase
        .from('vendor_inquiries')
        .select(`
          *,
          user:user_profiles!user_id(
            id,
            full_name,
            email,
            phone
          ),
          vendor:vendor_profiles!vendor_id(
            id,
            business_name,
            category,
            phone,
            email
          )
        `)
        .eq('id', inquiryId)
        .single();

      if (error) throw error;

      return {
        success: true,
        data
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get conversation messages for an inquiry
   */
  async getConversationMessages(inquiryId) {
    try {
      const { data, error } = await supabase
        .from('conversation_messages')
        .select('*')
        .eq('inquiry_id', inquiryId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      return {
        success: true,
        data: data || []
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  }

  /**
   * Send a message in a conversation
   */
  async sendMessage(inquiryId, messageText, senderType = 'user') {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('conversation_messages')
        .insert({
          inquiry_id: inquiryId,
          sender_id: user.id,
          sender_type: senderType,
          message_text: messageText,
          is_read: false
        })
        .select()
        .single();

      if (error) throw error;

      return {
        success: true,
        data
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Mark messages as read
   */
  async markMessagesAsRead(inquiryId) {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabase.rpc('mark_messages_as_read', {
        p_inquiry_id: inquiryId,
        p_user_id: user.id
      });

      if (error) throw error;

      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get unread message count for a user
   */
  async getUnreadMessageCount(userId) {
    try {
      const { data, error } = await supabase
        .from('unread_message_counts')
        .select('unread_count')
        .eq('user_id', userId)
        .single();

      if (error) throw error;

      return {
        success: true,
        count: data?.unread_count || 0
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        count: 0
      };
    }
  }
}

export default new InquiryService();
